import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  ActivateAdminInput,
  AdminUser,
  AdminUsersListResponse,
  CreateAdminUserInput,
  UpdateAdminUserInput,
  VerifyTokenResponse,
} from "../types";

const adminUserSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().nullable(),
  roles: z.array(z.string()),
  status: z.enum(["pending", "active", "disabled"]),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});

const adminUsersListResponseSchema = z.object({
  data: z.array(adminUserSchema),
  total: z.number(),
});

const verifyTokenResponseSchema = z.object({
  valid: z.boolean(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export const fetchAdminUsers = async (): Promise<AdminUsersListResponse> => {
  const response = await apiClient.get("/admin/users");
  return adminUsersListResponseSchema.parse(response.data);
};

export const createAdminUser = async (input: CreateAdminUserInput): Promise<AdminUser> => {
  const response = await apiClient.post("/admin/users", input);
  return adminUserSchema.parse(response.data);
};

export const updateAdminUser = async (
  id: string,
  input: UpdateAdminUserInput
): Promise<AdminUser> => {
  const response = await apiClient.put(`/admin/users/${id}`, input);
  return adminUserSchema.parse(response.data);
};

export const deleteAdminUser = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/users/${id}`);
};

export const resendInvitation = async (id: string): Promise<void> => {
  await apiClient.post(`/admin/users/${id}/resend-invitation`);
};

export const verifyActivationToken = async (token: string): Promise<VerifyTokenResponse> => {
  const response = await apiClient.get(`/admin/activate/${token}`);
  return verifyTokenResponseSchema.parse(response.data);
};

export const activateAdmin = async (input: ActivateAdminInput): Promise<AdminUser> => {
  const response = await apiClient.post("/admin/activate", input);
  return adminUserSchema.parse(response.data);
};
